import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X, CheckCircle2, Crown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useNavigate } from "react-router-dom";
import { EyeCaptainLogo } from "@/components/brand/EyeCaptainLogo";

interface FreePlanPopupProps {
  onClose: () => void;
}

const FREE_PLAN_OPTIONS = [
  {
    el: {
      headline: "\u039c\u03b7\u03bd \u03c4\u03bf \u03c7\u03ac\u03c3\u03b5\u03b9\u03c2!",
      title: "\u039a\u03ac\u03b8\u03b5 \u03bc\u03ad\u03c1\u03b1 \u03c7\u03c9\u03c1\u03af\u03c2 optimization = \u03c7\u03b1\u03bc\u03ad\u03bd\u03b1 \u03bb\u03b5\u03c6\u03c4\u03ac",
      subtitle: "\u039f\u03b9 \u03b1\u03bd\u03c4\u03b1\u03b3\u03c9\u03bd\u03b9\u03c3\u03c4\u03ad\u03c2 \u03c3\u03bf\u03c5 \u03ae\u03b4\u03b7 \u03b2\u03b5\u03bb\u03c4\u03b9\u03ce\u03bd\u03bf\u03c5\u03bd \u03c4\u03b1 sites \u03c4\u03bf\u03c5\u03c2.",
    },
    en: {
      headline: "Don't miss this!",
      title: "Every day without optimization = lost money",
      subtitle: "Your competitors are already improving their sites. What are you waiting for?",
    },
  },
  {
    el: {
      headline: "Mind = Blown",
      title: "30 Credits \u03b3\u03b9\u03b1 2 CRO \u03b1\u03bd\u03b1\u03bb\u03cd\u03c3\u03b5\u03b9\u03c2. \u0394\u03c9\u03a1\u0395\u0391\u039d.",
      subtitle: "\u03a7\u03c9\u03c1\u03af\u03c2 \u03ba\u03ac\u03c1\u03c4\u03b1, \u03c7\u03c9\u03c1\u03af\u03c2 \u03ba\u03c1\u03c5\u03c6\u03ad\u03c2 \u03c7\u03c1\u03b5\u03ce\u03c3\u03b5\u03b9\u03c2. \u0391\u03c0\u03bb\u03ac \u03b1\u03c0\u03bf\u03c4\u03b5\u03bb\u03ad\u03c3\u03bc\u03b1\u03c4\u03b1.",
    },
    en: {
      headline: "Mind = Blown",
      title: "30 Credits for 2 CRO audits. Free.",
      subtitle: "No card, no hidden fees. Just results.",
    },
  },
];

const getRandomOption = () => FREE_PLAN_OPTIONS[Math.floor(Math.random() * FREE_PLAN_OPTIONS.length)];

export function FreePlanPopup({ onClose }: FreePlanPopupProps) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [option] = useState(getRandomOption);
  const dynamicCopy = option[language] || option.en;

  const t = {
    el: {
      features: [
        "30 Credits (2 CRO \u0391\u03bd\u03b1\u03bb\u03cd\u03c3\u03b5\u03b9\u03c2)",
        "AI recommendations",
        "Neuromarketing insights",
        "\u0392\u03bb\u03ad\u03c0\u03b5\u03b9\u03c2 \u03c4\u03b9 \u03b2\u03bb\u03ad\u03c0\u03bf\u03c5\u03bd \u03bf\u03b9 \u03c0\u03b5\u03bb\u03ac\u03c4\u03b5\u03c2 \u03c3\u03bf\u03c5"
      ],
      cta: "\u0398\u03ad\u03bb\u03c9 \u03bd\u03b1 \u03b1\u03c5\u03be\u03ae\u03c3\u03c9 \u03c4\u03b9\u03c2 \u03c0\u03c9\u03bb\u03ae\u03c3\u03b5\u03b9\u03c2 \u03bc\u03bf\u03c5",
      badge: "\u0394\u03c9\u03a1\u0395\u0391\u039d",
      noThanks: "\u0398\u03b1 \u03c3\u03c5\u03bd\u03b5\u03c7\u03af\u03c3\u03c9 \u03bd\u03b1 \u03b1\u03b3\u03bd\u03c9 \u03c4\u03b1 \u03c0\u03c1\u03bf\u03b2\u03bb\u03ae\u03bc\u03b1\u03c4\u03b1...",
    },
    en: {
      features: [
        "30 Credits (2 CRO Audits)",
        "AI recommendations",
        "Neuromarketing insights",
        "See what your customers see"
      ],
      cta: "I want to boost my sales",
      badge: "FREE",
      noThanks: "I'll keep ignoring the problems...",
    },
  };

  const text = t[language] || t.en;

  const handleStartFree = () => {
    onClose();
    navigate("/signup?plan=free");
  };

  return (
    <div className="relative p-6 md:p-8">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground transition-colors"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex flex-col items-center text-center">
        <EyeCaptainLogo
          alt="EyeCaptain"
          className="h-8 w-auto mb-4 opacity-80"
        />

        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 text-[hsl(var(--ec-mint))] rounded-full text-xs font-medium mb-3">
          <Crown className="h-3 w-3" />
          {text.badge}
        </span>

        <h2 className="text-xl font-bold mb-1">
          {dynamicCopy.headline}
        </h2>
        <p className="text-base font-semibold text-[hsl(var(--ec-mint))] mb-2">
          {dynamicCopy.title}
        </p>
        <p className="text-sm text-muted-foreground mb-5 leading-relaxed max-w-xs mx-auto">
          {dynamicCopy.subtitle}
        </p>

        <div className="space-y-1.5 mb-5 w-full max-w-xs mx-auto text-left">
          {text.features.map((feature, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 className="h-3 w-3 text-[hsl(var(--ec-mint))]" />
              </div>
              <span>{feature}</span>
            </div>
          ))}
        </div>

        <Button
          onClick={handleStartFree}
          size="lg"
          className="w-full max-w-xs h-11 text-sm font-semibold rounded-xl bg-primary hover:bg-primary"
        >

          {text.cta}
        </Button>

        <div className="mt-4 text-center">
          <button
            onClick={onClose}
            className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors italic"
          >
            {text.noThanks}
          </button>
        </div>
      </div>
    </div>
  );
}
